/* Rouxproject Lookbook v1.1.0 — vanilla JS, no carousel dependency. */
(() => {
  if (customElements.get('roux-lookbook')) return;

  class RouxLookbook extends HTMLElement {
    connectedCallback() {
      // Theme-editor inserts can connect this element before its children exist.
      this.setupFrame = requestAnimationFrame(() => this.setup());
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.setupFrame);
      cancelAnimationFrame(this.transitionFrame);
      cancelAnimationFrame(this.viewportFrame);
      this.viewportObserver?.disconnect();
      this.abort?.abort();
    }

    setup() {
      this.abort?.abort();
      this.viewportObserver?.disconnect();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.cards = Array.from(this.querySelectorAll('[data-rx-card]')).reverse();
      this.stage = this.querySelector('[data-rx-stage]');
      if (!this.stage || !this.cards.length) return;

      this.index = 0;
      this.gesture = null;
      this.suppressClickUntil = 0;
      this.counter = this.querySelector('[data-rx-counter]');
      this.status = this.querySelector('[data-rx-status]');
      this.controls = this.querySelector('[data-rx-controls]');
      this.media = matchMedia('(max-width: 950px)');
      this.phone = matchMedia('(max-width: 480px)');
      this.querySelector('[data-rx-prev]').addEventListener('click', () => this.go(this.index - 1), { signal });
      this.querySelector('[data-rx-next]').addEventListener('click', () => this.go(this.index + 1), { signal });
      this.media.addEventListener('change', () => this.layout(), { signal });
      this.phone.addEventListener('change', () => this.layout(), { signal });
      window.addEventListener('resize', () => this.scheduleViewportMeasure(), { signal });
      window.visualViewport?.addEventListener('resize', () => this.scheduleViewportMeasure(), { signal });
      // Includes customised Dawn headers and announcement bars. Write only when
      // the document offset changes, avoiding feedback from our own height update.
      this.viewportObserver = new ResizeObserver(() => this.scheduleViewportMeasure());
      this.viewportObserver.observe(document.body);
      this.stage.addEventListener('pointerdown', (event) => this.startGesture(event), { signal });
      this.stage.addEventListener('pointermove', (event) => this.moveGesture(event), { signal });
      this.stage.addEventListener('pointerup', () => this.endGesture(), { signal });
      this.stage.addEventListener('pointercancel', () => this.endGesture(), { signal });
      this.stage.addEventListener('lostpointercapture', (event) => {
        // Touch starts with implicit capture on the image. Moving capture to its
        // enclosing link releases the image; that transfer must not end the swipe.
        const gesture = this.gesture;
        if (gesture && event.target === gesture.captureTarget && !gesture.captureTarget.hasPointerCapture(event.pointerId)) this.endGesture();
      }, { signal });
      this.stage.addEventListener('pointerleave', () => {
        if (this.gesture?.pointerType === 'mouse' && !this.gesture.captured) this.endGesture();
      }, { signal });
      this.stage.addEventListener('dragstart', (event) => {
        if (this.media.matches) event.preventDefault();
      }, { signal });
      this.stage.addEventListener('click', (event) => {
        if (Date.now() < this.suppressClickUntil) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, { capture: true, signal });
      this.addEventListener('keydown', (event) => this.onKey(event), { signal });
      document.addEventListener('shopify:block:select', (event) => {
        const selected = this.cards.findIndex((card) => card.dataset.blockId === String(event.detail?.blockId));
        if (selected >= 0) this.go(selected, false, true);
      }, { signal });

      this.layout();
      this.classList.add('is-ready');
    }

    layout() {
      this.endGesture();
      this.classList.add('no-transition');
      this.classList.toggle('is-mobile', this.media.matches);
      this.controls.hidden = !this.media.matches || this.cards.length < 2 || this.dataset.showControls !== 'true';
      if (this.media.matches) {
        this.measureViewport();
        this.render(false);
      } else {
        this.style.removeProperty('--rx-page-top');
        this.cards.forEach((card) => {
          card.removeAttribute('style');
          card.removeAttribute('aria-hidden');
          card.inert = false;
          card.classList.remove('is-active');
          card.querySelector('a')?.removeAttribute('tabindex');
        });
        this.status.textContent = '';
      }
      cancelAnimationFrame(this.transitionFrame);
      this.transitionFrame = requestAnimationFrame(() => {
        this.transitionFrame = requestAnimationFrame(() => this.classList.remove('no-transition'));
      });
    }

    scheduleViewportMeasure() {
      cancelAnimationFrame(this.viewportFrame);
      this.viewportFrame = requestAnimationFrame(() => this.measureViewport());
    }

    measureViewport() {
      if (!this.media.matches || !this.isConnected) return;
      const top = Math.max(0, Math.round(this.getBoundingClientRect().top + window.scrollY));
      const value = `${top}px`;
      if (this.style.getPropertyValue('--rx-page-top') !== value) this.style.setProperty('--rx-page-top', value);
    }

    render(announce = true) {
      const total = this.cards.length;
      const scaleStep = this.phone.matches ? 0.17 : 0.14;
      const translateStep = this.phone.matches ? 24 : 30;
      const rotateStep = this.phone.matches ? 14 : 11;
      const blurStep = this.phone.matches ? 4 : 6;
      const focusedCard = this.cards.find((card) => card.contains(document.activeElement));
      // Transfer focus before hiding/inerting the previous link.
      if (focusedCard && focusedCard !== this.cards[this.index]) {
        const next = this.cards[this.index];
        next.inert = false;
        next.removeAttribute('aria-hidden');
        next.querySelector('a')?.removeAttribute('tabindex');
        if (next.querySelector('a')) next.querySelector('a').focus({ preventScroll: true });
        else document.activeElement.blur();
      }
      this.cards.forEach((card, cardIndex) => {
        let offset = cardIndex - this.index;
        if (Math.abs(offset) > total / 2) offset += offset > 0 ? -total : total;
        const distance = Math.abs(offset);
        const active = distance === 0;
        const visible = distance <= 2;
        card.style.zIndex = String(total - distance);
        card.style.opacity = visible ? '1' : '0';
        card.style.visibility = visible ? 'visible' : 'hidden';
        card.style.transform = `translate(-50%, -50%) translateY(${offset * translateStep}%) scale(${Math.max(0.1, 1 - distance * scaleStep)}) rotateX(${-offset * rotateStep}deg)`;
        card.style.filter = active ? 'none' : `blur(${Math.min(distance, 2) * blurStep}px) brightness(${Math.max(0.5, 1 - distance * 0.2)})`;
        card.classList.toggle('is-active', active);
        card.inert = !active;
        card.setAttribute('aria-hidden', String(!active));
        const link = card.querySelector('a');
        if (link) {
          if (active) link.removeAttribute('tabindex');
          else link.setAttribute('tabindex', '-1');
        }
      });
      this.counter.textContent = `${this.index + 1} / ${total}`;
      if (announce) {
        const title = this.cards[this.index].querySelector('.rx-lookbook__title')?.textContent.trim();
        this.status.textContent = `Collection ${this.index + 1} of ${total}${title ? ': ' + title : ''}`;
      }
    }

    go(nextIndex, announce = true, immediate = false) {
      this.index = ((nextIndex % this.cards.length) + this.cards.length) % this.cards.length;
      if (!this.media.matches) return;
      if (immediate) {
        this.layout();
      } else {
        this.render(announce);
      }
    }

    startGesture(event) {
      if (!this.media.matches || !event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
      const card = event.target.closest('[data-rx-card]');
      if (card !== this.cards[this.index]) return;
      this.gesture = {
        id: event.pointerId, pointerType: event.pointerType,
        x: event.clientX, y: event.clientY, moved: false, advanced: false,
        captureTarget: card.querySelector('a') || card, captured: false
      };
    }

    moveGesture(event) {
      const gesture = this.gesture;
      if (!gesture || gesture.id !== event.pointerId) return;
      const dx = event.clientX - gesture.x;
      const dy = event.clientY - gesture.y;
      if (Math.max(Math.abs(dx), Math.abs(dy)) > 10) {
        gesture.moved = true;
        this.suppressClickUntil = Date.now() + 600;
        this.classList.add('is-dragging');
        if (!gesture.captured) {
          gesture.captureTarget.setPointerCapture(event.pointerId);
          gesture.captured = true;
        }
      }
      const threshold = this.phone.matches ? 50 : 80;
      if (!gesture.advanced && Math.abs(dy) >= threshold && Math.abs(dy) > Math.abs(dx) * 1.15) {
        gesture.advanced = true;
        if (this.cards.length > 1) this.go(this.index + (dy < 0 ? 1 : -1));
      }
    }

    endGesture() {
      if (this.gesture?.moved) this.suppressClickUntil = Date.now() + 600;
      this.gesture = null;
      this.classList.remove('is-dragging');
    }

    onKey(event) {
      if (!this.media.matches || event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
      if (event.target.closest('input, textarea, select, [contenteditable="true"]')) return;
      const moves = { ArrowUp: this.index - 1, ArrowDown: this.index + 1, Home: 0, End: this.cards.length - 1 };
      if (!(event.key in moves)) return;
      event.preventDefault();
      this.go(moves[event.key]);
    }
  }
  customElements.define('roux-lookbook', RouxLookbook);
})();
